/**
 * Which bhajans have a meaning in the reader's language, and which have one
 * only in other languages.
 *
 * Its own request, on its own cache, because the answer cannot be read off the
 * slim catalog index: that index keeps every node's `edges` — so "does this
 * bhajan have a meaning at all" is free — but strips MEANING `data` to `{}`,
 * and the languages live in the data. Deriving it in the browser means
 * fetching all 728 meaning nodes untrimmed: 3.9MB raw, ~1.1MB gzipped,
 * measured against prod 2026-07-31. This artifact is ~2KB gzipped because it
 * carries nothing but bhajan ids; the names, deities and edges the list needs
 * are already in hand from the catalog.
 *
 * Keyed by language and evicted on a language change, UNLIKE the meaning text
 * in meaningsApi: that payload is untrimmed and carries all ten languages at
 * once, so it is language-independent. This one is a per-language answer.
 */

import axios from 'axios';
import { APP } from '../app/appConfig';

/** Matches services/api.tsx: bm sends no `a` param, ss sends `a=ss`. */
const appParams = APP.apiApp ? { a: APP.apiApp } : {};

export interface MeaningsIndex {
  /** Bhajans whose meaning has readable text in the requested language. */
  inLanguage: Set<number>;
  /** Bhajans whose meaning has readable text ONLY in other languages. */
  otherLanguages: Set<number>;
}

const cache = new Map<string, MeaningsIndex>();
const inFlight = new Map<string, Promise<MeaningsIndex>>();

/** Test seam. Not used in app code. */
export const __clearMeaningsIndexCache = () => {
  cache.clear();
  inFlight.clear();
};

/**
 * Real, positive, integral ids only.
 *
 * This body is untyped JSON that decides list membership. A string id, a float
 * or a null would each survive into a Set and then silently never match a
 * node, producing a list that is quietly short by exactly the malformed
 * entries — the kind of wrong that looks right.
 */
const toIdSet = (value: unknown): Set<number> => {
  const out = new Set<number>();
  if (!Array.isArray(value)) return out;
  for (const id of value) {
    if (typeof id === 'number' && Number.isSafeInteger(id) && id > 0) out.add(id);
  }
  return out;
};

/**
 * Fetch the meanings index for one language.
 *
 * Rejects rather than resolving empty on a transport or shape failure, so the
 * caller can tell "no bhajan has a meaning in this language" (a real, empty
 * answer) from "we could not find out". The two mean different things on
 * /browse: the first hides a tile, the second should not be mistaken for it.
 */
export async function fetchMeaningsIndex(languageCode: string): Promise<MeaningsIndex> {
  const cached = cache.get(languageCode);
  if (cached) return cached;

  const pending = inFlight.get(languageCode);
  if (pending) return pending;

  /**
   * The cleanup is attached AFTER the map is populated, not from a `finally`
   * inside the async body.
   *
   * If axios were to throw SYNCHRONOUSLY — a bad config, a throwing request
   * interceptor — the body would run to its `finally` before ever suspending,
   * so the delete would happen before the set, and the rejected promise would
   * sit in the map forever. Every later call for that language would then be
   * handed the same rejection, and the tiles would stay gone for the rest of
   * the session even after the network came back.
   */
  const request = (async () => {
    const response = await axios.get<unknown>('/api/graph/meanings/index', {
      // `platform` is mandatory on every graph endpoint; omitting it returns
      // a misleading 401 (queryParamPlatform.ts:14), not a 400.
      params: { platform: 'web4', l: languageCode, ...appParams },
    });

    /**
     * A 200 whose body is not the shape we asked for is a FAILURE.
     *
     * The site's own index.html is served with a 200 for any path CloudFront
     * does not otherwise match, so a misrouted request yields the SPA shell
     * rather than an error status. Coercing that to two empty sets would
     * report, permanently and silently, that no bhajan anywhere has a meaning.
     */
    const body = response.data;
    if (!body || typeof body !== 'object') {
      throw new Error(`Unexpected meanings index body for language ${languageCode}`);
    }
    const raw = body as { inLanguage?: unknown; otherLanguages?: unknown };
    if (!Array.isArray(raw.inLanguage) && !Array.isArray(raw.otherLanguages)) {
      throw new Error(`Meanings index for ${languageCode} carries neither list`);
    }

    const index: MeaningsIndex = {
      inLanguage: toIdSet(raw.inLanguage),
      otherLanguages: toIdSet(raw.otherLanguages),
    };
    cache.set(languageCode, index);
    return index;
  })();

  inFlight.set(languageCode, request);
  // Detached from the promise the caller gets, so this cleanup can never turn a
  // rejection into an unhandled one, nor swallow it.
  request.catch(() => {}).then(() => {
    if (inFlight.get(languageCode) === request) inFlight.delete(languageCode);
  });
  return request;
}
